import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AppTheme } from '../themeConfig';
import { useLocation } from 'react-router-dom';

// Orientation types
export type Orientation = 'portrait' | 'landscape';

// Context interface
interface OrientationContextProps {
  orientation: Orientation;
  isMobilePortrait: boolean;
  isLandscape: boolean;
  isPortrait: boolean;
}

// Default context values
const defaultContext: OrientationContextProps = {
  orientation: 'landscape',
  isMobilePortrait: false,
  isLandscape: true,
  isPortrait: false,
};

// Create context
const OrientationContext = createContext<OrientationContextProps>(defaultContext);

// Provider props
interface OrientationProviderProps {
  children: ReactNode;
}

// Throttle function to limit the frequency of executions
const throttle = (func: Function, delay: number) => {
  let lastCall = 0;
  return (...args: any[]) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      return func(...args);
    }
  };
};

// Provider component
export const OrientationProvider: React.FC<OrientationProviderProps> = ({ children }) => {
  const theme = useTheme<AppTheme>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  const [orientation, setOrientation] = useState<Orientation>('landscape');
  const [windowDimensions, setWindowDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  // Check if current path is a storefront path that should be exempt from rotation enforcement
  const isStorefrontPath = useMemo(() => {
    const storefrontPaths = [
      '/',
      '/signup',
      '/terms',
      '/privacy',
      '/signup/payment',
      '/resources',
      '/knowledge',
      '/blog',
      '/login',
      '/founders-statement',
      '/roadmap',
    ];
    return storefrontPaths.some(
      path =>
        location.pathname === path ||
        location.pathname.startsWith('/signup/') ||
        location.pathname.startsWith('/knowledge/')
    );
  }, [location.pathname]);

  // Handle resize with throttling for better performance
  const handleResize = throttle(() => {
    setWindowDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, 100);

  // Update dimensions on resize
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Use Screen Orientation API if available
      if (window.screen && 'orientation' in window.screen) {
        const screenOrientation = window.screen.orientation;

        const handleOrientationChange = () => {
          const type = screenOrientation.type;
          const isLandscape = type.includes('landscape');
          setOrientation(isLandscape ? 'landscape' : 'portrait');
        };

        screenOrientation.addEventListener('change', handleOrientationChange);
        // Initial orientation
        handleOrientationChange();

        // Fallback to resize for browsers without Screen Orientation API support
        window.addEventListener('resize', handleResize);

        return () => {
          screenOrientation.removeEventListener('change', handleOrientationChange);
          window.removeEventListener('resize', handleResize);
        };
      } else {
        // Fallback for browsers without Screen Orientation API
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
      }
    }
  }, [handleResize]);

  // Update orientation based on dimensions
  useEffect(() => {
    const { width, height } = windowDimensions;
    setOrientation(width > height ? 'landscape' : 'portrait');
  }, [windowDimensions]);

  // Memoize derived values to prevent unnecessary re-renders
  const contextValue = useMemo(() => {
    const isPortrait = orientation === 'portrait';
    const isLandscape = orientation === 'landscape';
    // Only enforce mobile portrait restriction for non-storefront pages
    const isMobilePortrait = isMobile && isPortrait && !isStorefrontPath;

    return {
      orientation,
      isMobilePortrait,
      isLandscape,
      isPortrait,
    };
  }, [orientation, isMobile, isStorefrontPath]);

  // Render orientation overlay for mobile portrait mode
  const OrientationOverlay = useMemo(() => {
    if (!contextValue.isMobilePortrait) return null;

    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'background.paper',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Box
          sx={{
            animation: 'rotate 2s infinite ease-in-out',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '@keyframes rotate': {
              '0%': { transform: 'rotate(0deg)' },
              '25%': { transform: 'rotate(90deg)' },
              '75%': { transform: 'rotate(90deg)' },
              '100%': { transform: 'rotate(0deg)' },
            },
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 120,
              border: '3px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: '80%',
                height: '40%',
                bgcolor: 'primary.main',
                borderRadius: 1,
              }}
            />
          </Box>
        </Box>
        <Typography variant="h6" align="center" sx={{ fontWeight: 'bold' }}>
          Please rotate your device
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mt: 1 }}>
          This application is optimized for landscape view
        </Typography>
      </Box>
    );
  }, [contextValue.isMobilePortrait]);

  return (
    <OrientationContext.Provider value={contextValue}>
      {children}
      {OrientationOverlay}
    </OrientationContext.Provider>
  );
};

// Custom hook for accessing orientation context
export const useOrientationContext = () => useContext(OrientationContext);

export default OrientationContext;
